import React, { Fragment, Suspense, useState, useEffect } from "react";
import { ThemeProvider, StyledEngineProvider, CssBaseline } from "@mui/material";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import theme from "./theme";
import GlobalStyles from "./GlobalStyles";
import { getCurrentUser } from 'aws-amplify/auth';

// Analytics and Cookie Consent
import { initializeAnalytics, page, isAnalyticsInitialized } from "./services/analytics";
import { hasConsent } from "./utils/cookieConsent";
import CookieBanner from "./components/CookieBanner/CookieBanner";

// import Pace from "./shared/components/Pace";

import Auth from "./Pages/Authentication/Auth";
import Tos from "./Pages/Tos/Tos";
import PrivacyPolicy from "./Pages/Tos/PrivacyPolicy";
import Portal from "./Pages/Portal/Portal";
import CheckoutSuccess from './Pages/CheckoutSuccess';
import UpgradeFailed from './Pages/UpgradeFailed';
import FooterNav from './components/footer/FooterNav';
import CookiePreferences from './Pages/CookiePreferences/CookiePreferences';

// External redirect component
function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return <div>Redirecting...</div>;
}

// <Pace color={theme.palette.primary.light} />

function PrivateRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        await getCurrentUser();
        setIsAuthenticated(true); // User is authenticated
      } catch {
        setIsAuthenticated(false); // User is not authenticated
      }
    };

    checkAuthentication();
  }, []);

  if (isAuthenticated === null) {
    return <div>Loading...</div>; // Show loading while checking authentication
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" />;
  }

  return children;
};

function PublicRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        await getCurrentUser();
        setIsAuthenticated(true); // User is authenticated
      } catch {
        setIsAuthenticated(false); // User is not authenticated
      }
    };

    checkAuthentication();
  }, []);

  if (isAuthenticated === null) {
    return <div>Loading...</div>; // Show loading while checking authentication
  }

  if (isAuthenticated) {
    return <Navigate to="/portal" />;
  }

  return children;
}

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!hasConsent()) {
      return;
    }

    const trackPageView = async () => {
      if (!isAnalyticsInitialized()) {
        try {
          await initializeAnalytics();
        } catch (error) {
          console.error('Failed to initialize analytics before page tracking', error);
          return;
        }
      }

      page(undefined, {
        path: `${location.pathname}${location.search}`,
      });
    };

    trackPageView();
  }, [location]);

  return null;
}

// function PrivateRoute({ children }) {
//   const [isAuthenticated, setIsAuthenticated] = useState(null);

//   if (!isAuthenticated) {
//     return <Navigate to="/auth/register" />;
//   }
//   return children;
// }


function App() {
  useEffect(() => {
    // Initialize analytics if user has already given consent
    if (hasConsent()) {
      initializeAnalytics();
    }
  }, []);
  return (
    <BrowserRouter>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyles />
          <style>{`
            * {
              font-family: "Montserrat", -apple-system, "system-ui", "Segoe UI", "Roboto", sans-serif !important;
            }
            #root {
              display: flex;
              flex-direction: column;
              min-height: 100vh;
            }
            .app-content {
              flex: 1;
              display: flex;
              flex-direction: column;
            }
          `}</style>

          {/* Cookie Consent Banner */}
          <CookieBanner />

          <AnalyticsTracker />

          <div className="app-content">
            <Suspense fallback={<Fragment />}>
              <Routes>
                {/* Redirect root and quickstart to SmartSpectra home */}
                <Route path="/" element={<ExternalRedirect to="https://smartspectra.presagetech.com/" />} />
                <Route path="/quickstart" element={<ExternalRedirect to="https://smartspectra.presagetech.com/" />} />
                <Route path="/auth/:authentication" element={
                    <PublicRoute>
                      <Auth />
                    </PublicRoute>
                  }  
                />
                <Route path="/tos" element={<Tos />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/cookie-preferences" element={<CookiePreferences />} />
                <Route 
                  path="/portal/*" 
                  element={
                  <PrivateRoute>
                    <Portal />
                  </PrivateRoute>
                  } 
                />
                <Route 
                  path="/success" 
                  element={
                  <PrivateRoute>
                    <CheckoutSuccess />
                  </PrivateRoute>
                  } 
                />
                <Route 
                  path="/upgrade-failed" 
                  element={
                  <PrivateRoute>
                    <UpgradeFailed />
                  </PrivateRoute>
                  } 
                />
                <Route path="*" element={<Navigate to="/auth/login" />} />
              </Routes>
            </Suspense>
          </div>
          <FooterNav />
        </ThemeProvider>
      </StyledEngineProvider>
    </BrowserRouter>
  );
}

export default App;
